// Central error-reporting hook.
//
// Historically the app swallowed errors: mutation `catch` blocks showed a toast
// and the Apollo error link logged at `console.debug` (invisible by default),
// so backend/GraphQL failures (e.g. schema-drift mutations) failed silently.
//
// Everything funnels through `reportError` so there is ONE place to plug in
// error-monitoring services.
//
// Sentry: initialized in main.js (plugins/sentry.js). @sentry/vue auto-captures
// Vue render errors, window errors and unhandled rejections, so the two call
// sites that re-report those (main.js) are skipped below to avoid filing each
// one twice. Every OTHER call site is an explicit `catch` block, and a caught
// error is neither an unhandled rejection nor a render error, so none of that
// automatic instrumentation ever sees it. For those, this hook is the only
// feed. Imported as the bare @sentry/vue package (not plugins/sentry.js) to
// keep `import.meta` out of this module, which is imported by unit tests.
// captureException is a safe no-op before init (dev, or no VITE_SENTRY_DSN).
import * as Sentry from '@sentry/vue'

// Sources @sentry/vue already captures on its own. See the note above.
const SENTRY_AUTO_CAPTURED = new Set(['vue', 'unhandledrejection'])

// Quiet during unit tests so jest output isn't polluted by intentional
// error-path assertions.
const isTest =
  typeof process !== 'undefined' && process.env.VUE_APP_TEST === 'unit'

/**
 * Report an application error to the console and to Sentry. Never throws.
 *
 * @param {unknown} error   The error/exception.
 * @param {object}  [context]  Extra structured context (source, operation, …).
 */
export function reportError(error, context = {}) {
  if (isTest) return
  // Separate try blocks on purpose: a console failure must not cost us the
  // Sentry report, which is the only copy that outlives the browser session.
  try {
    console.error('[BowVue]', context.source || 'error', context, error)
  } catch {
    // Reporting must never break the app.
  }
  try {
    if (!SENTRY_AUTO_CAPTURED.has(context.source)) {
      Sentry.captureException(error, {
        tags: { source: context.source || 'error' },
        extra: context,
      })
    }
  } catch {
    // Reporting must never break the app.
  }
}

export default reportError
